/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {AugmentedBrakeData, BrakeChamberPosition, BrakeChamberStatusType} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {ExpandableListItem} from '@ion/dls/src/components/lists/ExpandableListItem';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import SensorListItem from './SensorListItem';

export interface SensorOverViewProps {
  data: AugmentedBrakeData
  columns?: any[];
  highlight?: BrakeChamberPosition;
  setHighlight?: Function;
  tableData?: any[]
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const contentStyles = getAssetDetailsContentStyles();
  return {
    overviewListWrapper : {
      [theme.breakpoints.down('sm')] : {
        display        : 'flex',
        justifyContent : 'center',
        alignItems     : 'center'
      }
    },
    overviewList : {
      border                         : `solid 1px ${dlsColor('grey5')}`,
      paddingBottom                  : '0px',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        minWidth : 300
      }
    },
    overviewListHeader : {
      ...dlsTypography('body3'),
      borderTop : `solid 1px ${dlsColor('grey5')}`,
      height    : '40px'
    },
    overviewListItem : {
      ...dlsTypography('body3'),
      borderTop : `solid 1px ${dlsColor('grey5')}`,
      height    : '40px',
      '&:hover' : {
        backgroundColor : dlsColor('paleBlue2'),
        borderTop       : `solid 1px ${dlsColor('aaBlue')}`,
        border          : `solid 1px ${dlsColor('aaBlue')}`
      }
    },
    selectedItem : {
      backgroundColor : dlsColor('paleBlue2'),
      border          : `solid 1px ${dlsColor('aaBlue')}`
    },
    itemRow : {
      padding    : '8px',
      whiteSpace : 'nowrap'
    },
    // current theme only - the bordered list becomes a stack of bordered
    // tile rows (8px gap), matching the Asset Details Drawer's other
    // sections, instead of the legacy single-bordered, underlined list ...
    currentOverviewList : {
      border        : 'none',
      display       : 'flex',
      flexDirection : 'column',
      gap           : '8px'
    },
    currentOverviewListHeader : {
      height  : 'auto',
      border  : 'none !important',
      padding : '0 8px'
    },
    currentOverviewListItem : {
      ...contentStyles.tile,
      height          : 'auto',
      border          : `1px solid ${dlsColor('grey7')} !important`,
      backgroundColor : `${dlsColor('white')} !important`
    },
    // current theme only - the expanded details sit flush under their row,
    // without the selected row's tinted padding or side borders ...
    currentExpandedWrapper : {
      backgroundColor : 'transparent !important'
    },
    currentExpandedItem : {
      padding         : '0 !important',
      border          : 'none !important',
      backgroundColor : 'transparent !important',
      '&:hover'       : {
        backgroundColor : 'transparent !important'
      }
    },
    currentSelectedItem : {
      border : `1px solid ${dlsColor('aaBlue')} !important`
    }
  };
});

/**
 *  Brake Chamber Detail section of the smarter trailer content.
 *
 * ```typescript
 *  import {BrakeChamberOverview} from '@ion/features/src/smarterTrailer';
 *
 *  <BrakeChamberOverview
 *    trailerData={trailerData}
 *    highlight='frontLeft'
 *    setHighlight={onHighlight} />
 * ```
 */
const BrakeChamberOverview = ({
  data,
  columns = [],
  highlight,
  setHighlight,
  tableData
}: SensorOverViewProps) => {
  const classes      = useStyles();
  const {isCurrent}  = useThemeContext();

  const brakePositionMap = {
    'rear-L'  : BrakeChamberPosition.rearLeft,
    'front-L' : BrakeChamberPosition.frontLeft,
    'rear-R'  : BrakeChamberPosition.rearRight,
    'front-R' : BrakeChamberPosition.frontRight
  };

  return (
    <div className={classes.overviewListWrapper}>
      <List className={themedClassName(classes.overviewList, classes.currentOverviewList, isCurrent)}>
        <ListItem className={themedClassName(classes.overviewListHeader, classes.currentOverviewListHeader, isCurrent)}>
          <SensorListItem header
            columnContent={columns} />
        </ListItem>
        {
          tableData?.map((array, i) => {
            if (array) {
              const itemId = brakePositionMap[array[0].value];
              const parkingData = data?.[itemId]?.parking;
              const isEngaged = parkingData.isEngaged;
              let status: string;
              if (parkingData?.value === null) {
                status = BrakeChamberStatusType.NotAvailable;
              } else {
                status = isEngaged
                  ? BrakeChamberStatusType.Engaged
                  : BrakeChamberStatusType.Released;
              }
              const expandedArr = [
                null,
                translate(`common:smarterTrailer.brakeChamber.${status}`),
                null,
                null];
              return (
                <ExpandableListItem highlightSelected
                  itemOpen={itemId === highlight}
                  disableRipple
                  disableGutters
                  itemId={itemId}
                  itemSelected={itemId === highlight}
                  onItemHover={setHighlight}
                  expandedWrapperClass={isCurrent ? classes.currentExpandedWrapper : undefined}
                  expandedItemClass={isCurrent ? classes.currentExpandedItem : undefined}
                  listClass={classnames(
                    classes.overviewListItem,
                    classes.itemRow,
                    highlight === itemId && classes.selectedItem,
                    isCurrent && classes.currentOverviewListItem,
                    isCurrent && highlight === itemId && classes.currentSelectedItem
                  )}
                  listItem={
                    <SensorListItem itemId={itemId}
                      columnContent={array}/>
                  }
                  expandedItem={
                    <SensorListItem itemId={itemId}
                      columnContent={expandedArr} />
                  } />
              );
            }
          })
        }
      </List>
    </div>
  );
};

export default BrakeChamberOverview;
